import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import logo from "../../assets/1.png";
import { Link, useLocation } from "react-router-dom";
import { 
  FaTachometerAlt, 
  FaBars, 
  FaTimes, 
  FaUser, 
  FaCog,
  FaBell,
  FaChevronDown
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { selectUser, selectIsAuthenticated, selectIsLoading } from "../../Apis/store/authSlice";
import LogoutButton from "../Logout/LogoutButton";

const Navbar = ({ bg = "bg-[#0F2A55]" }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const location = useLocation();
  
  // Refs for performance optimization
  const scrollTimeoutRef = useRef(null);
  const isScrollingRef = useRef(false);
  
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const isLoading = useSelector(selectIsLoading);

  // Get user role from context - memoized
  const selectedRole = useMemo(() => user?.role || "", [user?.role]);

  // Throttled scroll handler for better performance
  const handleScroll = useCallback(() => {
    if (isScrollingRef.current) return;
    
    isScrollingRef.current = true;
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Throttle scroll events
    scrollTimeoutRef.current = setTimeout(() => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);
      
      // Scroll spy functionality - optimized
      const sections = ['academy', 'services', 'features', 'call'];
      const scrollPosition = scrollY + 100;
      
      // Use requestAnimationFrame for smooth updates
      requestAnimationFrame(() => {
        for (let i = sections.length - 1; i >= 0; i--) {
          const element = document.getElementById(sections[i]);
          if (element && element.offsetTop <= scrollPosition) {
            setActiveSection(sections[i]);
            break;
          }
        }
        isScrollingRef.current = false;
      });
    }, 16); // ~60fps throttling
  }, []);

  // Handle scroll effect and scroll spy
  useEffect(() => {
    // Add smooth scrolling behavior to the document
    document.documentElement.style.scrollBehavior = 'smooth';
    
    // Use passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial call to set active section on mount
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Clear timeout on cleanup
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      // Reset scroll behavior on cleanup
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, [handleScroll]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear all timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      // Reset scrolling flag
      isScrollingRef.current = false;
    };
  }, []);

  // Optimized click outside handlers
  const handleUserMenuClickOutside = useCallback((event) => {
    if (isUserMenuOpen && !event.target.closest('.user-menu')) {
      setIsUserMenuOpen(false);
    }
  }, [isUserMenuOpen]);

  const handleMobileMenuClickOutside = useCallback((event) => {
    if (isMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.mobile-menu-button')) {
      setIsMenuOpen(false);
    }
  }, [isMenuOpen]);

  // Close user menu when clicking outside
  useEffect(() => {
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleUserMenuClickOutside);
      return () => document.removeEventListener('mousedown', handleUserMenuClickOutside);
    }
  }, [isUserMenuOpen, handleUserMenuClickOutside]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleMobileMenuClickOutside);
      return () => document.removeEventListener('mousedown', handleMobileMenuClickOutside);
    }
  }, [isMenuOpen, handleMobileMenuClickOutside]);

  // Memoized navigation items
  const navItems = useMemo(() => [
    { href: "#academy", label: "عن الأكاديمية", id: "academy" },
    { href: "#services", label: "خدماتنا", id: "services" },
    { href: "#features", label: "مميزاتنا", id: "features" },
    { href: "#call", label: "اتصل بنا", id: "call" }
  ], []);

  // Optimized smooth scroll to section
  const handleSmoothScroll = useCallback((e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      const offsetTop = element.offsetTop - 100; // Account for fixed navbar height
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
    // Close mobile menu after clicking
    setIsMenuOpen(false);
  }, []);

  // Memoized menu toggle handlers
  const toggleMobileMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const toggleUserMenu = useCallback(() => {
    setIsUserMenuOpen(prev => !prev);
  }, []);

  // Memoized class names for better performance
  const navClassName = useMemo(() => 
    `fixed top-0 w-full py-2 md:py-3 ${
      isScrolled ? `${bg} shadow-lg backdrop-blur-md bg-opacity-95` : 'bg-opacity-20 backdrop-blur-xs'
    } text-white z-50 transition-all duration-300`,
    [isScrolled, bg]
  );

  return (
    <nav 
      className={navClassName}
      dir="rtl"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Mobile menu button */}
       <button
            className="mobile-menu-button md:hidden p-2 rounded-md text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
         onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <FaTimes className="h-6 w-6" />
            ) : (
              <FaBars className="h-6 w-6" />
            )}
      </button>

          {/* Logo */}
          <Link 
            to="/" 
            className="flex-shrink-0 flex items-center gap-2 "
          >
            <img 
              src={logo} 
              alt="Logo" 
              className="h-14 md:h-20 w-auto transition-transform duration-200 hover:scale-105 drop-shadow-lg"
            />
      </Link>
    
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8 ">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleSmoothScroll(e, item.id)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 relative group drop-shadow-lg cursor-pointer ${
                  activeSection === item.id 
                    ? 'text-gray-300' 
                    : 'text-white hover:text-gray-300'
                }`}
              >
                {item.label}
                <span className={`absolute bottom-0 left-0 h-0.5 transition-all duration-200 ${
                  activeSection === item.id 
                    ? 'w-full bg-gray-300' 
                    : 'w-0 bg-gray-300 group-hover:w-full'
                }`}></span>
              </a>
            ))}
      </div>

          {/* Right side content */}
          <div className="flex items-center gap-4 ">
            {/* Loading indicator */}
      {isLoading && (
              <div className="flex items-center gap-2 ">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="text-white text-sm drop-shadow-lg">جاري التحميل...</span>
        </div>
      )}

            {/* Authenticated user menu */}
      {isAuthenticated && !isLoading && (
              <div className="relative user-menu">
                <div className="flex items-center gap-3 ">
                  {/* Dashboard link for admin/student */}
                  {(selectedRole === "أدمن" || selectedRole === "طالب") && (
                    <Link 
                      to={selectedRole === "أدمن" ? "/dashboard/GeneralLooka" : "/dashboard/GeneralLooks"}
                      className="p-2 text-white hover:text-[#0F2A55] hover:bg-white hover:bg-opacity-10 rounded-md transition-all duration-200"
                      title="لوحة التحكم"
                    >
                      <FaTachometerAlt className="h-5 w-5" />
      </Link>
    )}

                  {/* User profile dropdown */}
                  <div className="relative">
                    <button
                      onClick={toggleUserMenu}
                      className="flex items-center gap-2  p-2 text-white hover:text-[#0F2A55] hover:bg-white hover:bg-opacity-10 rounded-md transition-all duration-200"
                    >
                      <div className="w-8 h-8 bg-[#0F2A55] rounded-full flex items-center justify-center">
                        <FaUser className="h-4 w-4 text-white" />
                      </div>
                      <span className="hidden sm:block text-sm font-medium drop-shadow-lg">
                        {user?.name || "المستخدم"}
                      </span>
                      <FaChevronDown className={`h-3 w-3 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown menu */}
                    {isUserMenuOpen && (
                      <div className="absolute  left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-900">{user?.name || "المستخدم"}</p>
                          <p className="text-xs text-gray-500">{selectedRole}</p>
                        </div>
                    
                        <div className="border-t border-gray-200">
                          <LogoutButton className="w-full text-right px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200">
                            تسجيل خروج
    </LogoutButton>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Guest user buttons */}
            {!isAuthenticated && !isLoading && (
              <div className="flex items-center gap-3 ">
                <Link
                  to="/login"
                  className="text-white hover:text-gray-300 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 drop-shadow-lg"
                >
                  تسجيل الدخول
                </Link>
                <Link
                  to="/SingUP"
                  className="bg-[#0F2A55] hover:bg-blue-800 text-white px-6 py-2 rounded-md text-sm font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  اشترك الآن
                </Link>
  </div>
)}
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={`mobile-menu ${
            isMenuOpen ? 'block' : 'hidden'
          } md:hidden transition-all duration-300 ease-in-out`}
        >
          <div className="px-2 pt-2 pb-3 space-y-1 bg-[#0F2A55] bg-opacity-95 backdrop-blur-md rounded-lg mt-2 shadow-lg">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleSmoothScroll(e, item.id)}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 drop-shadow-lg cursor-pointer ${
                  activeSection === item.id 
                    ? 'text-gray-300' 
                    : 'text-white hover:text-gray-300 hover:bg-white hover:bg-opacity-20'
                }`}
              >
                {item.label}
              </a>
            ))}
            
            {/* Mobile auth buttons */}
        {!isAuthenticated && !isLoading && (
              <div className="pt-4 border-t border-white border-opacity-20">
                <Link
                  to="/login"
                  className="block px-3 py-2 text-white hover:text-gray-300 hover:bg-white hover:bg-opacity-20 rounded-md text-base font-medium transition-all duration-200 drop-shadow-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  تسجيل الدخول
        </Link>
                <Link
                  to="/SingUP"
                  className="block px-3 py-2 bg-[#0F2A55] hover:bg-blue-800 text-white rounded-md text-base font-semibold transition-all duration-200 mt-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  اشترك الآن
       </Link>
              </div>
      )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
